import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, PaperPlaneTilt, ChartLine, QrCode, ArrowRight, CheckCircle, Eye, MapPin } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, m] = await Promise.all([api.get("/analytics/summary"), api.get("/messages")]);
      setStats(s.data); setRecent(m.data.slice(0, 5));
    })();
  }, []);

  return (
    <div data-testid="dashboard-page">
      {user?.is_admin && (
        <div className="mb-6 rounded-2xl p-4 sm:p-5 border border-brand-primary/30 bg-brand-primary/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-welcome-banner">
          <div>
            <div className="font-heading font-bold">You're signed in as {user?.is_regional_admin ? `Regional Admin (${user?.region_slug})` : "Super Admin"}.</div>
            <div className="text-sm text-brand-text/75 mt-0.5">
              Head to the Admin console to see {user?.is_regional_admin ? "your region's" : "all regions'"} economics, vendors, and end-customer signups.
            </div>
          </div>
          <Link to="/app/admin" data-testid="dash-admin-cta"
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-4 py-2 text-sm font-semibold shrink-0">
            Open Admin →
          </Link>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Dashboard</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Hello, {user?.name?.split(" ")[0] || "Vendor"}.</h1>
          <p className="text-brand-text/70 mt-1">Here's how <span className="font-semibold text-brand-text">{user?.business_name}</span> is doing.</p>
        </div>
        <Link to="/app/qr" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-5 py-3 text-sm font-semibold transition-colors" data-testid="dash-qr-link">
          <QrCode size={18} weight="duotone" /> View my QR
        </Link>
      </div>

      {user && !user.is_admin && (!user.phone || !user.city || !user.state) && (
        <div className="mb-6 rounded-2xl p-4 sm:p-5 border border-brand-warning/40 bg-brand-warning/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="profile-completion-banner">
          <div className="flex items-start gap-3">
            <MapPin size={22} weight="duotone" className="text-brand-warning shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-bold">Complete your business profile</div>
              <div className="text-sm text-brand-text/75 mt-0.5">
                Add your {[!user.phone && "phone", !user.city && "city", !user.state && "state"].filter(Boolean).join(", ")} so we can put you on the Vendorly India map and reach you when it matters.
              </div>
            </div>
          </div>
          <Link to="/app/settings" data-testid="complete-profile-btn"
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-4 py-2 text-sm font-semibold shrink-0">
            Update now <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Customers" value={stats?.total_customers ?? "—"} testid="stat-customers" />
        <Stat icon={PaperPlaneTilt} label="Messages sent" value={stats?.total_messages ?? "—"} testid="stat-messages" />
        <Stat icon={CheckCircle} label="Delivery rate" value={stats ? `${stats.delivery_rate}%` : "—"} testid="stat-delivery" />
        <Stat icon={Eye} label="Read rate" value={stats ? `${stats.read_rate}%` : "—"} testid="stat-read" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-brand-border rounded-2xl p-6" data-testid="signups-chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary">Last 14 days</div>
              <h3 className="font-heading text-xl font-bold mt-1">Customer signups</h3>
            </div>
            <ChartLine size={22} weight="duotone" className="text-brand-primary" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.series || []}>
                <CartesianGrid stroke="#E5E0D8" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8B8682" fontSize={11} tickFormatter={(v)=>v.slice(5)} />
                <YAxis stroke="#8B8682" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#D97757" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-brand-border rounded-2xl p-6" data-testid="recent-messages-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">Recent messages</h3>
            <Link to="/app/messages" className="text-sm text-brand-primary font-semibold hover:underline inline-flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-brand-textMuted py-8 text-center">No campaigns yet.</div>
          ) : (
            <ul className="space-y-3">
              {recent.map(m => (
                <li key={m.id} className="p-3 rounded-xl border border-brand-border hover:border-brand-borderHover transition-colors">
                  <div className="text-sm line-clamp-2">{m.content}</div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-brand-textMuted">
                    <span>{m.recipients_count} sent</span>
                    <span>·</span>
                    <span>{m.delivered_count} delivered</span>
                    <span>·</span>
                    <span>{new Date(m.sent_at).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-5 hover:-translate-y-0.5 transition-transform duration-300" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">{label}</div>
        <Icon size={20} weight="duotone" className="text-brand-primary" />
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}
